import { useEffect, useState, useCallback } from "react";
import { Trophy, Flame, Snowflake, Target } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

// AGT-15 — small clickable challenge/score badge. Click to expand full detail.
export default function ChallengeBadge({ refreshSignal }) {
  const [d, setD] = useState(null);

  const load = useCallback(() => {
    api.get("/gamification/status").then((r) => setD(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load, refreshSignal]);

  if (!d) return null;

  const ch = d.challenge;
  const pct = d.level_progress?.pct ?? 0;

  return (
    <Popover onOpenChange={(o) => { if (o) load(); }}>
      <PopoverTrigger asChild>
        <button
          data-testid="challenge-badge"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-elevated px-3 py-1.5 text-xs font-semibold text-primary hover:border-secondary transition-colors"
          aria-label="Your challenges and score"
        >
          <Trophy size={13} className="text-secondary" />
          <span className="tabular-nums">Lvl {d.level}</span>
          <span className="text-muted-foreground tabular-nums">· {d.score} pts</span>
          {ch && <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-secondary animate-pulse" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-4" data-testid="challenge-panel">
        <div className="flex items-center justify-between">
          <div className="text-overline">Your challenge</div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Flame size={12} className="text-primary" /> {d.streak_app}d
            <Snowflake size={12} className="ml-1.5 text-blue-400" /> {d.freeze_tokens}
          </div>
        </div>

        {/* Level progress */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-primary">Level {d.level}</span>
            <span className="tabular-nums text-muted-foreground">{d.score} pts</span>
          </div>
          <div className="mt-1 h-1.5 w-full rounded-full bg-surface overflow-hidden">
            <div className="h-full rounded-full bg-secondary transition-all" style={{ width: `${pct}%`, minWidth: "4px" }} data-testid="challenge-level-bar" />
          </div>
          {d.level < 10 && (
            <div className="mt-1 text-[10px] text-muted-foreground tabular-nums">
              {d.level_progress.next_at - d.score} pts to level {d.level + 1}
            </div>
          )}
        </div>

        {/* Active challenge */}
        {ch ? (
          <div className="mt-4 rounded-xl border border-border bg-surface p-3" data-testid="challenge-active">
            <div className="flex items-center gap-1.5 text-sm font-medium text-primary">
              <Target size={14} className="text-secondary" /> {ch.lbl}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">Day {ch.day} of {ch.of} · {ch.tgt} {ch.unit}/day</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {ch.days_hit.map((v, i) => (
                <span
                  key={i}
                  title={`Day ${i + 1}`}
                  className={`h-4 w-4 rounded-full text-[9px] flex items-center justify-center ${
                    v === true ? "bg-secondary text-white" : v === false ? "bg-destructive/20 text-destructive" : "bg-surface-elevated border border-border text-muted-foreground"
                  }`}
                >
                  {i + 1}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-xl border border-dashed border-border bg-surface p-3 text-xs text-muted-foreground" data-testid="challenge-none">
            No active challenge — log a meal or workout and KAAYA will set you a fresh one. 🎯
          </div>
        )}

        {/* History */}
        {d.history?.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Recent</div>
            <div className="space-y-1">
              {d.history.slice(0, 3).map((h, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-foreground/70">{h.status === "done" ? "✅ Completed" : "⏸ Paused"} · {h.dur}d</span>
                  <span className="tabular-nums text-muted-foreground">+{h.pts_earned}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
